/* global $, process */
import { convert } from "$convert-metadata";
import { bb } from "$json";
import { Ajax } from "$json/lib/ajax";
import { conf } from "$json/lib/conf";
import { escapeHTML } from "$json/lib/escape";
import { editPolicy } from "$json/lib/feature-queries";
import { _ } from "$json/lib/gettext";
import { fromApiServer } from "$json/lib/location";
import { relatesToAs } from "$json/lib/utils/dom.js";
import { positionalFormat } from "$json/lib/text-utils";
import { converter } from "$md-converter";
import { html, render } from "lit";
import { classMap } from "lit/directives/class-map.js";
import { live } from "lit/directives/live.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { pipe } from "ramda";
import { createStore } from "redux/es/redux.mjs";
import {
  checkDate,
  leadWithZeroes,
  onChangeDate,
  valueToDate
} from "../../js/lib/dates";
import {
  assoc,
  assocPath,
  compose,
  curry,
  either,
  eqBy,
  filter,
  groupBy,
  groupWith,
  has,
  hasPath,
  head,
  identity,
  ifElse,
  includes,
  join,
  last,
  lensProp,
  map,
  not,
  nthArg,
  over,
  path,
  pathEq,
  pick,
  prop,
  propEq,
  propOr,
  reverse,
  split,
  strictUniq,
  tryCatch,
  when
} from "../../js/lib/functional.js";
import { forbidden_printable_chars, passes } from "./interoperable-filename.js";

const err_too_large = "file is too large";
const err_mimetype = "mimetype is not accepted";
const err_required = "field is required";
const err_multiple = "one file max";
const err_duplicate_name = "duplicate file";
const err_duplicate_name_same_upload_id = "duplicate file, same upload id";
const err_filename_inoperable = "filename is too long";

const errorSummary = path(["error", "summary"]);
const errorCode = path(["error", "code"]);
const errorSubcode = path(["error", "subcode"]);

const text_file = "text_file";
const text_add_default = "text_add";
const text_remove = "text_remove";
const text_select_default = "text_select";

const max_filename_charlength = 100;

conf.ignoredErrorCodes = propOr([], "ignoredErrorCodes", conf).concat([53]);

const errorTranslations = {
  "Error 53/1200": {
    en: "Invalid file! Only the following extensions are allowed: {0}",
    nl: "Ongeldig bestand! Alleen de volgende extensies zijn toegestaan: {0}"
  },
  "Error 53/1100": {
    en: "Uploaded pdf file misses the right header",
    nl: "Het pdf heeft niet de juiste header"
  },
  "Error 53/1101": {
    en: "Standard pdf files are not allowed",
    nl: "Standaard pdf-bestanden zijn niet toegestaan"
  },
  "Error 53/1102": {
    en: "Encrypted pdf files are not allowed",
    nl: "Versleutelde pdf-bestanden zijn niet toegestaan"
  },
  "Error 53/1103": {
    en: "Pdf files with form fields are not allowed",
    nl: "Pdf-bestanden met formuliervelden zijn niet toegestaan"
  },
  "Error 53/1201": {
    en: "Uploaded bmp file misses the right header",
    nl: "Het bmp-bestand mist de juiste header"
  },
  "Error 53/1202": {
    en: "Uploaded gif file misses the right header",
    nl: "Het gif-bestand mist de juiste header"
  },
  "Error 53/1203": {
    en: "Uploaded png file misses the right header",
    nl: "Het png-bestand mist de juiste header"
  },
  "Error 53/1204": {
    en: "Uploaded xml or svg file misses the right header",
    nl: "Het xml- of svg-bestand mist de juiste header"
  },
  "Error 53/1205": {
    en: "Uploaded jpg file misses the right header",
    nl: "Het jpg-bestand mist de juiste header"
  },
  "Error 53/1206": {
    en: "Uploaded office file misses the right header",
    nl: "Het Office-bestand mist de juiste header"
  },
  "Error 53/1300": {
    en: "Uploading this file(type) is not allowed",
    nl: "Het is niet toegestaan dit bestand(stype) te uploaden"
  }
};

const translations = {
  ...errorTranslations,
  [text_file]: {
    nl: "Document",
    en: "File"
  },
  [text_remove]: {
    nl: "verwijder",
    en: "remove"
  },
  [text_add_default]: {
    nl: "voeg toe",
    en: "add"
  },
  [text_select_default]: {
    nl: "Selecteer document",
    en: "Select file"
  },
  [err_too_large]: {
    nl: "Bestand ({0}) is te groot ({1}). Maximum toegestane grootte: {2}",
    en: "File ({0}) is too large ({1}). Maximum file size allowed: {2}"
  },
  [err_mimetype]: {
    nl: "Alleen de volgende bestandsformaten worden ondersteund: ",
    en: "We just support the following file types: "
  },
  [err_required]: {
    nl: "{0} is een verplicht veld",
    en: "{0} is a required field"
  },
  [err_multiple]: {
    nl: "Er geldt voor deze upload een maximum van een bestand",
    en: "One file max"
  },
  [err_duplicate_name]: {
    nl: "Dit bestand (of een met dezelfde naam) is al eerder ingestuurd",
    en: "This file, or a file with the same name, has been uploaded before"
  },
  [err_duplicate_name_same_upload_id]: {
    nl: "Dit bestand (of een met dezelfde naam) staat al in de lijst",
    en: "This file, or a file with the same name, is already in the list"
  },
  [err_filename_inoperable]: {
    nl: `De bestandsnaam kan problemen opleveren. Geef het bestand een naam van maximaal ${max_filename_charlength} zonder speciale tekens als: ${forbidden_printable_chars.join(
      " "
    )} of controletekens.`,
    en: `The file name could cause problems. Please give the file a name of max ${max_filename_charlength}, without special characters such as: ${forbidden_printable_chars.join(
      " "
    )}) or control characters.`
  }
};

$(() => _.addTranslations(translations));

const ERROR = "error";
const CREDS = "creds";
const SPECS = "specs";
const FILES = "files";
const SETTAG = "set tag";
const UNSETDATETAGS = "unset viewable tags";
const OPEN = "open";

const overProp = curry((fn, prop, ob) => assoc(prop, fn(ob[prop]), ob));
const boolifyNodescriptBool = string => Boolean(Number(string));
const boolifyNodescriptProp = overProp(boolifyNodescriptBool);

// Default mimetype to "".
// Translate 'pdfe' (encrypted) and 'pdff' (forms) to pdf
const translateSpecialPDFInMimetype = compose(
  join(","),
  strictUniq,
  map(x => ([".pdfe", ".pdff"].includes(x) ? ".pdf" : x)),
  split(","),
  s => s || ""
);

const reducer = (state, { type, payload }) => {
  switch (type) {
    case CREDS:
      return ifElse(propEq(CREDS, payload.toString()), identity, () =>
        assoc(CREDS, payload.toString(), {})
      )(state);
    case ERROR:
      return assocPath([ERROR, payload.id || "global"], payload.message, state);
    case SPECS:
      return assoc(
        SPECS,
        payload.map(
          compose(
            boolifyNodescriptProp("onebyone"),
            boolifyNodescriptProp("in_table"),
            boolifyNodescriptProp("multiple"),
            boolifyNodescriptProp("allow_duplicate_names"),
            boolifyNodescriptProp("preview"),
            overProp(Number, "maxmb"),
            overProp(
              when(
                Boolean,
                compose(converter.makeHtml, escapeHTML, s =>
                  s.replace(/\\n/g, "\n")
                )
              ),
              "text_help"
            ),
            overProp(translateSpecialPDFInMimetype, "mimetypes"),
            overProp(
              tryCatch(
                map(
                  compose(
                    boolifyNodescriptProp("required"),
                    boolifyNodescriptProp("list"),
                    tag =>
                      assoc(
                        "value",
                        tag.value ||
                          (tag.type === "dropdown" ? tag.options[0] : ""),
                        tag
                      )
                  )
                ),
                () => []
              ),
              "tags"
            )
          )
        ),
        state
      );
    case FILES:
      return assoc(
        FILES,
        payload.map(file => assoc("tags", parseTag(file.tag), file)),
        state
      );
    case SETTAG: {
      const { specid, name, value } = payload,
        spec = state.specs.find(propEq("id", specid)),
        tagindex = spec.tags.findIndex(propEq("name", name));
      return compose(
        assocPath(
          [SPECS, state.specs.indexOf(spec), "tags", tagindex, "edited"],
          true
        ),
        assocPath(
          [SPECS, state.specs.indexOf(spec), "tags", tagindex, "value"],
          value
        )
      )(state);
    }
    case OPEN: {
      const { specid, bool } = payload,
        spec = state.specs.find(propEq("id", specid));
      return assocPath([SPECS, state.specs.indexOf(spec), "open"], bool)(state);
    }
    case UNSETDATETAGS: {
      const { specid } = payload,
        spec = state.specs.find(propEq("id", specid));
      return assocPath(
        [SPECS, state.specs.indexOf(spec)],
        overProp(
          map(
            ifElse(
              propEq("type", "date"),
              compose(assoc("edited", false), assoc("value", "")),
              identity
            )
          ),
          "tags",
          spec
        )
      )(state);
    }
  }
  return state;
};

const store = createStore(
  reducer,

  (process.env.NODE_ENV === "development" &&
    window.__REDUX_DEVTOOLS_EXTENSION__ &&
    window.__REDUX_DEVTOOLS_EXTENSION__()) ||
    identity
);

const dateFormatServer = () => "yy-mm-dd";
const dateFormatUI = () => $.datepicker._defaults.dateFormat;
const eventIsDroppable = bool => event => bool && event.preventDefault();

const dateFormatConvert = (from, to) =>
  when(
    Boolean,
    compose(leadWithZeroes, value =>
      $.datepicker.formatDate(to(), valueToDate(from(), value))
    )
  );

const dateFormatConvertFromServerToUI = dateFormatConvert(
  dateFormatServer,
  dateFormatUI
);
const dateFormatConvertFromUIToServer = dateFormatConvert(
  dateFormatUI,
  dateFormatServer
);
const ext = path => path.split(".").pop().toLowerCase();

function acceptsMimetype(mimetypes, path) {
  return !mimetypes || mimetypes.includes(ext(path));
}

const earlierFileMatchingFilename = (filename, files) =>
  files.find(propEq("value", filename.toLowerCase()));

function setError(id, message) {
  store.dispatch({ type: ERROR, payload: { id, message } });
}

function clearError(id) {
  setError(id, null);
}

function humanSize(size) {
  if (size > 1024 * 1024)
    return (Math.round((size * 100) / (1024 * 1024)) / 100).toString() + "MB";
  else return (Math.round((size * 100) / 1024) / 100).toString() + "KB";
}

const addPicker =
  ({ minimum, maximum, specid, name }) =>
  event => {
    const target = event.target,
      $target = $(target);
    const firstTime = !target.classList.contains("hasDatepicker");

    $target.datepicker({
      showOn: "focus",
      minDate: dateFormatConvertFromServerToUI(minimum),
      maxDate: dateFormatConvertFromServerToUI(maximum),
      onSelect: value => {
        store.dispatch({
          type: SETTAG,
          payload: {
            specid,
            name,
            value: dateFormatConvertFromUIToServer(value)
          }
        });
      }
    });
    if (firstTime) $target.focus();
  };

const eventRemove =
  ({ file, groupid, id }) =>
  _event => {
    clearError(id);
    const body = new URLSearchParams();
    body.append("delete", "remove");
    body.append("deletable", file);
    getFiles({ method: "POST", urlparams: body.toString(), groupid, id });
  };

function focusFirstInputInRow() {
  const input = document.querySelector(
    ".p-upload__form-row :is(input, select):not([disabled])"
  );
  if (input) input.focus();
}

const eventSetOpenState =
  ({ specid, bool }) =>
  _event => {
    clearError(specid);
    store.dispatch({
      type: OPEN,
      payload: { specid, bool }
    });
    focusFirstInputInRow();
  };

let persistentUploaders = {};

const getAndSetPersistentUploader = (name, id) => {
  if (hasPath([name, id])(persistentUploaders))
    return path([name, id], persistentUploaders);
  const uploader = document.createElement("div");
  uploader.classList.add("p-upload");
  persistentUploaders = assocPath([name, id], uploader, persistentUploaders);
  return uploader;
};

const insertAsNeeded = (position, target, node) => {
  if (!document.contains(node) || !relatesToAs(position, target, node))
    target.insertAdjacentElement(position, node);
};

const onStoreChange = () => {
  const state = store.getState();
  const specs = state[SPECS] || [];
  compose(
    nodes => {
      map(key => {
        // Node has no uploader anymore?
        if (map(head, nodes).indexOf(key) === -1) {
          for (let uploader of Object.values(persistentUploaders[key])) {
            uploader.parentNode && uploader.parentNode.removeChild(uploader);
          }
          delete persistentUploaders[key];
        } else {
          compose(
            map(([_node, uploaders]) => {
              for (let uploader of Object.values(persistentUploaders[key])) {
                if (uploaders.indexOf(uploader) === -1) {
                  uploader.parentNode &&
                    uploader.parentNode.removeChild(uploader);
                  // Do not remove from persistentuploaders, they may be restored
                }
              }
            }),
            filter(pathEq([0], key))
          )(nodes);
        }
      })(Object.keys(persistentUploaders));
    },
    map(allSpecs => {
      const node = allSpecs[0].node;
      const group = document.querySelector(`.group[data-node="${node}"]`);
      const uploaders = [];
      if (group) {
        compose(
          map(positionedSpecs => {
            const uploader = getAndSetPersistentUploader(
              node,
              positionedSpecs[0].id
            );
            uploaders.push(uploader);
            render(
              positionedSpecs.map(
                compose(hfile, spec => assoc("state", state, spec))
              ),
              uploader
            );
            const position = propOr("afterEnd", "position", positionedSpecs[0]);
            if (Number.isNaN(Number(position))) {
              if (
                includes(position, [
                  "afterBegin",
                  "afterEnd",
                  "beforeBegin",
                  "beforeEnd"
                ])
              ) {
                insertAsNeeded(position, group, uploader);
                return;
              }
            } else {
              const formGroup =
                group.querySelectorAll(`.form-group`)[Number(position)];
              if (formGroup) {
                insertAsNeeded("afterEnd", formGroup, uploader);
                return;
              }
            }
            // FallBack to default
            insertAsNeeded("afterEnd", group, uploader);
          }),
          Object.values,
          groupBy(prop("position"))
        )(allSpecs);
      }
      // Return all uploaders within this node?
      return [node, uploaders];
    }),
    groupWith(eqBy(prop("groupid")))
  )(specs);
};

store.subscribe(onStoreChange);

const discardEvent = fun => (ev, data) => fun(data);

const addUploaders = data => {
  const uploads = compose(
    arr => arr.flatMap(getUploads),
    propOr([], "groups")
  )(data);
  if (uploads.length) {
    const creds = new URLSearchParams();
    creds.set("uniqueid", data.uniqueid);
    creds.set("modelid", data.modelid);
    creds.set("sessionid", data.sessionid);
    creds.set("fmt", "json");
    store.dispatch({ type: CREDS, payload: creds });
    store.dispatch({ type: SPECS, payload: uploads });
    getFiles({ method: "POST" });
  } else if (has("groups")(data)) {
    store.dispatch({ type: SPECS, payload: [] });
    store.dispatch({
      type: FILES,
      payload: []
    });
  }
};

const getUploads = group => {
  if (!group.metadata) {
    return [];
  }
  const metadata = convert(group.metadata);
  if (!metadata.uploads) {
    return [];
  }
  return metadata.uploads
    .filter(({ id }) => id !== undefined)
    .map(compose(assoc("node", group.name), assoc("groupid", group.groupid)));
};

const parseTag = s =>
  Object.fromEntries(
    s
      .split(/\n/g)
      .map(line => [
        line.substr(0, line.indexOf("=")),
        line.substr(1 + line.indexOf("="))
      ])
  );

const validates = tryCatch(
  pipe(
    arr => arr.map(checkValue),
    arr => arr.reduce((acc, cur) => acc === true && cur === true, true)
  ),
  () => false
);

const getFiles = ({
  method = "POST",
  body = null,
  urlparams = "",
  groupid,
  id
}) =>
  Ajax.post({
    url: `getfiles?${store.getState()[CREDS]}&${urlparams}`,
    method,
    dataType: "json",
    data: body,
    cache: false,
    contentType: false,
    processData: false
  }).done(json => {
    if (json.error) {
      if (errorCode(json) === 53) {
        const translation = _(`Error ${errorCode(json)}/${errorSubcode(json)}`);
        if (errorSubcode(json) === 1200) {
          // Extensions
          setError(
            id,
            positionalFormat(
              translation,
              compose(last, split(": "), errorSummary)(json)
            )
          );
        } else {
          setError(id, translation);
        }
      } else {
        setError(id, errorSummary(json));
      }
    } else {
      store.dispatch({
        type: FILES,
        payload: json.filelist
      });
    }
    // groupid is given when interacting with the uploader. If it is
    // not the current group, go back and (maybe) forth.
    if (groupid) {
      bf(groupid, bb.getVar("screenid"));
    }
  });

const hfile = ({
  id,
  text,
  text_help,
  mimetypes,
  multiple,
  onebyone,
  maxmb,
  preview,
  tags,
  state,
  in_table,
  open,
  text_select = _(text_select_default),
  text_add = _(text_add_default),
  groupid
}) => {
  const files =
    (state[FILES] && state[FILES].filter(file => file.tags.id === id)) || [];
  const viewableTags = tags.filter(propEq("list", true));
  const editableTags = compose(
    map(assoc("specid", id)),
    filter(either(propEq("type", "date"), propEq("type", "dropdown")))
  )(tags);
  const hasReachedMax = !multiple && files.length > 0;
  const disabled = compose(not, validates)(editableTags) || hasReachedMax;
  const uploadFields = [
    editableTags.map(htag),
    hlabel(
      {
        cm: {
          "p-upload__fileinput--disabled": disabled,
          "p-upload__fileinput": true
        },
        text: text_select,
        reversed: true
      },
      html`<input
        @dragover=${eventIsDroppable(not(disabled))}
        @dragenter=${eventIsDroppable(not(disabled))}
        @drop=${eventUpload(id)}
        @change=${eventUpload(id)}
        ?multiple=${multiple && !onebyone}
        ?disabled=${disabled}
        accept=${mimetypes}
        type="file"
        class=${classMap({
          "p-upload__fileinput__elem": true,
          "p-upload__fileinput__elem--in-table": in_table
        })}
        data-maxsize=${maxmb}
      />`
    )
  ];

  return html`
    <div
      class="p-upload__uploader"
      data-p-upload__id="${id}"
      data-p-upload__preview="${preview}"
      data-p-upload__multiple="${multiple}"
      data-p-upload__onebyone="${onebyone}"
      data-p-upload__hasfiles="${files.length > 0}"
    >
      <div class="p-upload__texts">
        <h3 class="p-upload__heading">${text}</h3>
        ${text_help
          ? html`<div class="p-upload__helptext">${unsafeHTML(text_help)}</div>`
          : ""}
      </div>
      ${(!in_table &&
        !hasReachedMax &&
        html`
          <form class="p-upload__form">
            <fieldset class="p-upload__fieldset"> ${uploadFields} </fieldset>
            ${(state[ERROR] &&
              html`
                <div class="p-upload__notice">${state[ERROR][id]}</div>
              `) ||
            ""}
          </form>
        `) ||
      ""}
      ${(((in_table && open) || files.length > 0) &&
        html`
          <table class="p-upload__uploads">
            <thead>
              <tr>
                ${(in_table ? reverse : identity)([
                  html` <th>${_(text_file)}</th>`,
                  viewableTags.map(
                    tag => html`
                      <th
                        data-p-upload--uploads-tag="${tag.name}"
                        data-p-upload--uploads-type="${tag.type}"
                        >${tag.text}</th
                      >
                    `
                  )
                ])}
              </tr>
            </thead>
            <tbody>
              ${files.map(
                file => html`
                  <tr>
                    ${(in_table ? reverse : identity)([
                      html`<td
                        >${preview
                          ? html`
                              <figure class="p-upload__preview">
                                <img src="${fromApiServer(file.href)}" />
                                <figcaption>${file.value}</figcaption>
                              </figure>
                            `
                          : html`
                              <a
                                target="_blank"
                                href="${fromApiServer(file.href)}"
                                >${file.value}</a
                              >
                            `}</td
                      >`,
                      viewableTags.map(
                        tag => html`
                          <td
                            data-p-upload--uploads-tag="${tag.name}"
                            data-p-upload--uploads-type="${tag.type}"
                          >
                            ${tag.type === "date"
                              ? tryCatch(
                                  dateFormatConvertFromServerToUI,
                                  nthArg(1)
                                )(file.tags[tag.name])
                              : file.tags[tag.name]}
                          </td>
                        `
                      )
                    ])}
                    <td class="p-upload__uploads-cell-delete">
                      <button
                        type="button"
                        @click=${eventRemove({
                          file: file.value,
                          groupid,
                          id
                        })}
                      >
                        <span>${_(text_remove)}</span>
                      </button>
                    </td>
                  </tr>
                `
              )}
              ${in_table && open
                ? html`<tr class="p-upload__form-row"
                    >${compose(
                      map(field => html`<td>${field}</td>`),
                      ([tags, input]) => [...tags, input]
                    )(uploadFields)}<td
                      class="p-upload__uploadable-cell-delete"
                    >
                      <button
                        type="button"
                        @click=${eventSetOpenState({ specid: id, bool: false })}
                      >
                        <span>${_(text_remove)}</span>
                      </button>
                    </td>
                  </tr>`
                : ""}
            </tbody>
          </table>
        `) ||
      ""}
      ${(in_table &&
        !hasReachedMax &&
        html`<button
          class="bb-record-add p-upload__add-row-button"
          type="button"
          ?disabled="${open}"
          @click=${eventSetOpenState({ specid: id, bool: true })}
        >
          <span>${text_add}</span>
        </button>`) ||
      ""}
      ${(in_table &&
        state[ERROR] &&
        html` <div class="p-upload__notice">${state[ERROR][id]}</div> `) ||
      ""}
    </div>
  `;
};

const hlabel = ({ text, cm, reversed = false }, ...children) => html`
  <div class=${classMap(cm)}>
    <label
      >${(reversed ? reverse : identity)([
        html`<span>${text}</span>`,
        children
      ])}</label
    >
  </div>
`;

const checkValue = tag => {
  if (tag.type === "date")
    return checkDate(
      assoc("notnull", tag.required, tag),
      tryCatch(dateFormatConvertFromServerToUI, nthArg(1))(tag.value)
    );
  return true;
};

const hinput = ({
  type,
  required,
  minimum,
  maximum,
  options,
  name,
  specid,
  classes,
  value,
  _error
}) => {
  switch (type) {
    case "dropdown":
      return html`
        <select
          class="${classes}"
          name="${name}"
          data-type="${type}"
          @change=${event => {
            store.dispatch({
              type: SETTAG,
              payload: {
                specid,
                name,
                value: event.target.value
              }
            });
          }}
          >${options.map(option => html` <option>${option}</option> `)}</select
        >
      `;
    case "date":
      return html`
        <input
          @change=${event => {
            onChangeDate(event);
            store.dispatch({
              type: SETTAG,
              payload: {
                specid,
                name,
                value: tryCatch(
                  dateFormatConvertFromUIToServer,
                  nthArg(1)
                )(event.target.value)
              }
            });
          }}
          .value=${live(
            tryCatch(dateFormatConvertFromServerToUI, nthArg(1))(value)
          )}
          @focus=${addPicker({ minimum, maximum, specid, name })}
          autocomplete="off"
          ?required=${required}
          class="${classes}"
          name="${name}"
          data-type="${type}"
          placeholder="${_("dateplaceholder", _("select date"))}"
        />
      `;
    default:
      return "";
  }
};

const htag = tag => {
  let error = "";
  if (tag.edited) {
    try {
      checkValue(tag);
    } catch (err) {
      error = html` <div class="p-upload__notice">${err}</div> `;
    }
  }
  let input = hinput(
    compose(assoc("error", error), assoc("classes", "tag"))(tag)
  );
  // Not really necessary, never called with constant tag (gets it from store).
  if (tag.type === "constant") return input;
  return hlabel(
    {
      text: tag.text,
      cm: {
        "p-upload__tag--invalid": !!error,
        "p-upload__tag--group": true,
        "p-upload__tag--dropdown": tag.type === "dropdown",
        "p-upload__tag--date": tag.type === "date"
      }
    },
    input,
    error
  );
};

const eventUpload = id => event => {
  event.preventDefault();
  const target = event.target;
  const droppedFiles =
    event.type === "drop" ? event.dataTransfer.files : target.files;
  doUpload(id, droppedFiles, event);
  // Important - we do not want to double-send this one. NOTE: outside of lit-html!
  target.value = "";
};

const doUpload = (id, files, event) => {
  clearError(id);
  const state = store.getState();
  const spec = state.specs.find(propEq("id", id));

  if ((!spec.multiple || spec.onebyone) && files.length > 1) {
    return setError(id, _(err_multiple));
  }

  const iniline = ({ name, value }) => `${name}=${value}`;

  const uploads = [...files].map(file => {
    if (file.size > 1024 * 1024 * spec.maxmb) {
      return setError(
        id,
        bb.positionalFormat(
          _(err_too_large),
          file.name,
          humanSize(file.size),
          `${spec.maxmb}Mb`
        )
      );
    }

    if (!passes(file.name, max_filename_charlength)) {
      return setError(id, _(err_filename_inoperable));
    }

    if (!acceptsMimetype(spec.mimetypes, file.name)) {
      return setError(id, `${_(err_mimetype)} ${spec.mimetypes}`);
    }

    // @todo: new spec: sometimes they want only a check on files in the current session.
    // So: earlier.tags.id === id or earlier.tags.length !== 0
    // But not when earlier.tags.length === 0;
    if (!spec.allow_duplicate_names) {
      const earlier = earlierFileMatchingFilename(file.name, state.files);
      if (earlier) {
        if (earlier.tags.id === id)
          return setError(
            id,
            `${
              spec["err_duplicate_name_same_upload_id"] ||
              _(err_duplicate_name_same_upload_id)
            }: ${file.name}`
          );
        return setError(
          id,
          `${spec["err_duplicate_name"] || _(err_duplicate_name)}: ${file.name}`
        );
      }
    }

    const body = new FormData();
    const form = event.target.closest(".p-upload__form, .p-upload__form-row");
    const tag = [{ name: "id", value: id }]
      .concat(
        [...form.querySelectorAll(".tag")].map(
          when(
            pathEq(["dataset", "type"], "date"),
            pick(["value", "name"]),
            over(
              lensProp("value"),
              tryCatch(dateFormatConvertFromUIToServer, nthArg(1))
            )
          )
        )
      )
      .concat(spec.tags.filter(propEq("type", "constant")))
      .map(iniline)
      .join("\r\n");
    // Some intercepting backends (that want to do other stuff with
    // the file) have trouble retrieving addable.name. So just send it
    // as another parameter. Do this first though, as our own parser
    // gets confused when a parameter of the name "filename" follows
    // the actual file.
    body.append("filename", file.name);
    body.append("addable", file);
    body.append("tag", tag);
    body.append("groupid", spec.groupid);
    getFiles({ body, groupid: spec.groupid, id });
    return true;
  });

  if (filter(Boolean, uploads).length === uploads.length) {
    store.dispatch({
      type: UNSETDATETAGS,
      payload: {
        specid: id
      }
    });
    store.dispatch({
      type: OPEN,
      payload: { specid: id, bool: false }
    });

    focusFirstInputInRow();
  }
};

const bf = (prev, current) => {
  if (prev !== current)
    bb.gotonode(
      prev,
      () => {
        editPolicy(conf) === "return" && bb.gotonode(current);
      },
      { cbIsAdditional: true }
    );
};

$(document).on("bb:postHandleData", discardEvent(addUploaders));
$(document).on("bb:hasUpdated", discardEvent(addUploaders));
